import { api } from "@clarent/backend/convex/_generated/api";
import { useRouterState } from "@tanstack/react-router";
import { useMutation } from "convex/react";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { sessionReplayUrl } from "../lib/analytics";
import { errorMessage } from "../lib/convexError";

/** Decision D45: the sidebar's "Help and feedback" box. Messages are saved and emailed to us. */
export function FeedbackDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const send = useMutation(api.feedback.send);
  const page = useRouterState({ select: (s) => s.location.pathname });
  const [message, setMessage] = useState("");
  const [state, setState] = useState<{ status: "editing" | "sending" | "sent" } | { status: "error"; text: string }>({ status: "editing" });

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      // A send still in flight keeps its state, so reopening can't hide it.
      setState((s) => (s.status === "sending" ? s : { status: "editing" }));
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const sent = message;
    setState({ status: "sending" });
    try {
      await send({ message: sent, page, replayUrl: sessionReplayUrl() });
      // Only clear the text that was sent. Anything typed since stays as a draft.
      setMessage((current) => (current === sent ? "" : current));
      setState({ status: "sent" });
    } catch (err) {
      setState({ status: "error", text: errorMessage(err) });
    }
  };

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onClick={(e) => e.target === ref.current && onClose()}
      aria-labelledby="feedback-title"
      className="panel m-auto w-[min(480px,calc(100vw-32px))] p-6 text-ink shadow-float backdrop:bg-ink/30"
    >
      <h2 id="feedback-title" className="app-title">
        Help and feedback
      </h2>
      {state.status === "sent" ? (
        <>
          <p className="mt-2 text-sm text-muted">Thanks, we've got it. If you asked a question, we'll reply by email.</p>
          <div className="mt-5 flex justify-end">
            <button type="button" className="btn-primary btn-md" onClick={onClose}>
              Close
            </button>
          </div>
        </>
      ) : (
        <form onSubmit={submit}>
          <p className="mt-2 text-sm text-muted">Tell us what's getting in the way or what you'd change. A rough note is fine.</p>
          <label htmlFor="feedback-message" className="label mt-4">
            Your message
          </label>
          <textarea
            id="feedback-message"
            className="input"
            rows={5}
            maxLength={4000}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            autoFocus
          />
          {state.status === "error" && <p className="notice-error mt-3">{state.text}</p>}
          <div className="mt-5 flex justify-end gap-2">
            <button type="button" className="btn-secondary btn-md" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary btn-md" disabled={!message.trim() || state.status === "sending"}>
              {state.status === "sending" ? "Sending…" : "Send"}
            </button>
          </div>
        </form>
      )}
    </dialog>
  );
}
